/**
 * Coin Award Calculator — pure function that computes coin rewards for any mode.
 *
 * Returns total coins (base + trick grade bonus). Does NOT call addCoins() —
 * the caller handles the side effect.
 */

import type { GameState } from '../types';
import { COIN_AWARD } from './catalog';
import { GRADE_THRESHOLDS } from '../constants/tricks';

/** Compute the trick-grade coin bonus from the best trick score in the match. */
function gradeBonus(trickScores: number[]): number {
  const best = Math.max(0, ...trickScores);
  for (const { grade, minScore } of GRADE_THRESHOLDS) {
    if (best >= minScore) {
      if (grade === 'S') return COIN_AWARD.GRADE_S_BONUS;
      if (grade === 'A') return COIN_AWARD.GRADE_A_BONUS;
      return 0; // B/C/D = no bonus
    }
  }
  return 0;
}

/** Compute total coin award for the current game state. */
export function computeCoinAward(state: GameState): number {
  const ms = state.modeState;
  const bonus = gradeBonus(state.trickScores);

  switch (ms.mode) {
    case 'vs_local':
      return COIN_AWARD.WIN + COIN_AWARD.LOSS + bonus;

    case 'vs_online': {
      const mySlot = state.multiplayer?.mySlot ?? 0;
      const won = state.winner === mySlot;
      return (won ? COIN_AWARD.WIN : COIN_AWARD.LOSS) + bonus;
    }

    case 'classic':
    case 'cosmic_lottery':
      return COIN_AWARD.WIN + COIN_AWARD.LOSS + bonus;

    case 'survival':
      return COIN_AWARD.SURVIVAL_BASE + COIN_AWARD.SURVIVAL_PER_KILL * ms.survivalKills + bonus;

    case 'roguelite':
      return COIN_AWARD.ROGUELITE_PER_ZONE * ms.rogueliteZone + bonus;

    case 'ascent':
      return (state.players[0].alive ? COIN_AWARD.ASCENT_COMPLETE : COIN_AWARD.ASCENT_DEATH) + bonus;

    case 'target_blast': {
      if (!ms.runComplete) return 0;
      const base = COIN_AWARD.TARGET_BLAST_COMPLETE;
      // bestTimes is updated before game_over, so index 0 is the current best.
      // If the player's time IS the new best, they get the bonus.
      const newBest = ms.bestTimes[0] !== undefined && ms.bestTimes[0] >= ms.elapsedTime
        ? COIN_AWARD.TARGET_BLAST_NEW_BEST : 0;
      return base + newBest + bonus;
    }

    case 'training':
    case 'tutorial':
      return 0;

    default:
      return 0;
  }
}
